import LocalizedLink from '@components/Localization/LocalizedLink'
import { Box, Image, Text, theme } from '@gassan-ui'
import React, { FC, useState } from 'react'
import AllOrPriceTitleVariant from './AllOrPriceTitleVariant'
import * as S from './elements'
import Header from './Header'
import Images from './Images'
import PriceOnlyVariant from './PriceOnlyVariant'
import { WidthProps } from 'styled-system'
import { imgix } from '@lib/imigx'
import { externalProducts } from '@config/externalProducts'
import * as ReactGA from 'react-ga'

export type ProductCardVariant = 'all' | 'price-title' | 'price-only'

type Props = WidthProps & {
  images?: string[]
  as?: string
  brand?: string
  href?: string
  id?: string
  title?: string
  subtitle?: string
  price?: number
  discountPrice?: number
  label?: string
  isOnWishlist?: boolean
  onClickWishlist?: () => void
  isNoPopover?: boolean
  isLoading?: boolean
  variant: ProductCardVariant
  promotionLabel?: string
  hasBlendMode?: boolean
}

export const ProductCard: FC<Props> = ({
  images,
  as,
  brand,
  href = '/p/[slug]',
  id,
  title,
  subtitle,
  price,
  discountPrice,
  isOnWishlist,
  onClickWishlist,
  isLoading,
  isNoPopover = false,
  label,
  variant,
  width,
  promotionLabel,
  hasBlendMode
}) => {
  const [hasMouseEntered, setHasMouseEntered] = useState(false)
  const alt = `${title} ${subtitle ? subtitle : brand ? brand : ''}`
  const handleMouseEnter = () => {
    if (typeof window.ontouchstart === 'undefined' && images.length > 1) {
      setHasMouseEntered(true)
    }
  }
  const isExternal = id && typeof externalProducts[id] !== 'undefined'

  const handleMouseLeave = () => setHasMouseEntered(false)
  const inner = (
    <S.Anchor
      data-test-id={!isLoading && 'product-card'}
      variant={variant}
      isLoading={isLoading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      width={width}
      hasBlendMode={hasBlendMode}
      href={isExternal ? externalProducts[id] : as}
      target={isExternal ? '_blank' : '_self'}
      onClick={() => {
        ReactGA.event({
          category: 'Ecommerce',
          action: 'productClick'
        })
      }}
    >
      <S.BackgroundContainer>
        <Header
          isOnWishlist={isOnWishlist}
          onClickWishlist={onClickWishlist}
          isLoading={isLoading}
          label={label}
        ></Header>
        <S.ImagesContainer suppressHydrationWarning>
          {isLoading ? null : (
            <>
              {images.length > 1 &&
              typeof window !== 'undefined' &&
              typeof window.ontouchstart === 'undefined' ? (
                <Images images={images} title={alt} mouseEntered={hasMouseEntered} />
              ) : (
                <Image
                  placeholderSrc={imgix(images[0], { width: 50 })}
                  sources={[
                    imgix(images[0], { width: 350 }),
                    imgix(images[0], { width: 350 }),
                    imgix(images[0], { width: 450 })
                  ]}
                  position="absolute"
                  top={0}
                  alt={alt}
                  ratio={1}
                  loading="lazy"
                />
              )}
            </>
          )}
        </S.ImagesContainer>
        {promotionLabel && (
          <Box
            position="absolute"
            bottom={{ _: '.75rem', medium: '1.5rem' }}
            right={{ _: '.5rem', medium: '1.5rem' }}
            pt={{ _: '.1rem', medium: '.25rem' }}
            pb={{ _: '.2rem', medium: '.25rem' }}
            px={{ _: '.5rem', medium: '.75rem' }}
            bg={theme.colors.bigStone}
            width="auto"
            textAlign="right"
          >
            <Text as="span" color="white" variant="small" fontWeight="bold" lineHeight="1">
              {promotionLabel}
            </Text>
          </Box>
        )}
      </S.BackgroundContainer>
      {['all', 'price-title'].includes(variant) ? (
        <AllOrPriceTitleVariant
          variant={variant}
          title={title}
          subtitle={subtitle}
          price={price}
          discountPrice={discountPrice}
          isLoading={isLoading}
        />
      ) : (
        <PriceOnlyVariant isLoading={isLoading} price={price} discountPrice={discountPrice} />
      )}
    </S.Anchor>
  )

  // Don't wrap with LocalizedLink if it is externals
  if (isLoading || isExternal) {
    return inner
  }

  return (
    <LocalizedLink
      href={{
        pathname: href,
        query: !isNoPopover && { popover: true }
      }}
      as={as}
    >
      {inner}
    </LocalizedLink>
  )
}

export default ProductCard
